import { getRouteApi } from "@tanstack/react-router";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  Phone,
  Mail,
  MessageCircle,
  MapPin,
  ArrowLeft,
  CheckCircle2,
  Navigation,
  ArrowRight,
} from "lucide-react";
import { getLocation, locations } from "@/data/locations";
import { SERVICES } from "@/data/services";
import { LocationDeepLinks } from "@/components/LocationDeepLinks";
import { EntityPanel } from "@/components/EntityPanel";
import { consolidateGraph } from "@/lib/schema-graph";
import { programmaticRating } from "@/lib/programmatic-ratings";
import { BUILD_DATE, speakableSchema, faqPageSchema } from "@/lib/aeo";

const routeApi = getRouteApi("/locations/$slug");

export function LocationPage() {
  const { loc } = routeApi.useLoaderData();

  const related = locations
    .filter((l) => l.region === loc.region && l.slug !== loc.slug)
    .slice(0, 9);

  const serviceList = Object.values(SERVICES);

  return (
    <main className="min-h-screen bg-gradient-to-b from-navy-deep via-navy to-navy-deep text-white">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10 px-4 py-14 sm:px-6 sm:py-20">
        <div
          className="absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,hsl(var(--gold)/0.2),transparent_60%)]"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-5xl">
          <Link
            to="/locations"
            className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-gold/80 hover:text-gold"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> พื้นที่ให้บริการทั้งหมด
          </Link>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-gold">
            <MapPin className="h-3 w-3" aria-hidden="true" />
            {loc.type === "headquarters"
              ? "สำนักงานใหญ่"
              : loc.type === "branch"
                ? "สำนักงานสาขา"
                : loc.type === "bkk-district"
                  ? "สำนักงานตัวแทน · กรุงเทพฯ"
                  : loc.type === "transit"
                    ? "จุดบริการใกล้ BTS/MRT"
                    : loc.type === "mall"
                      ? "จุดนัดพบในห้างสรรพสินค้า"
                      : loc.type === "expat-hub"
                        ? "ย่านเอ็กซ์แพท · จุดบริการพิเศษ"
                        : "สำนักงานตัวแทน · ต่างจังหวัด"}
          </div>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-5xl">{loc.nameTh}</h1>
          <p className="mt-1 text-sm text-white/80">
            {loc.nameEn} · {loc.region}
          </p>

          {loc.description && (
            <p className="mt-5 max-w-3xl text-base text-white/80">{loc.description}</p>
          )}

          {loc.address && (
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/85 backdrop-blur">
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 flex-none text-gold" aria-hidden="true" />
                <div>
                  <div className="font-semibold">ที่อยู่</div>
                  <div>
                    {loc.address}
                    {loc.postalCode ? ` ${loc.postalCode}` : ""}
                  </div>
                  {loc.landmarksTh?.length ? (
                    <div className="mt-2 text-white/75">
                      <span className="font-semibold text-gold">จุดสังเกต:</span>{" "}
                      {loc.landmarksTh.join(" · ")}
                    </div>
                  ) : null}
                  {loc.transitTh ? (
                    <div className="mt-1 text-white/75">
                      <span className="font-semibold text-gold">การเดินทาง:</span> {loc.transitTh}
                    </div>
                  ) : null}
                  {loc.mapUrl ? (
                    <a
                      href={loc.mapUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-gold hover:underline"
                    >
                      เปิดใน Google Maps →
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {/* Interactive Map + Directions */}
          <LocationMap loc={loc} />

          {/* Contact buttons */}
          <div className="mt-6 flex flex-wrap gap-2">
            <a
              href="tel:0832494999"
              aria-label="โทร 083-249-4999"
              className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-gold px-4 py-2.5 text-xs font-bold text-navy-deep transition hover:bg-gold/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep"
            >
              <Phone className="h-4 w-4" aria-hidden="true" /> 083-249-4999
            </a>
            <a
              href="https://line.me/ti/p/@nycli"
              target="_blank"
              rel="noreferrer"
              aria-label="แชท LINE @NYCLI"
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-bold text-white backdrop-blur transition hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep"
            >
              <MessageCircle className="h-4 w-4 text-gold" aria-hidden="true" /> LINE @NYCLI
            </a>
            <a
              href="mailto:contact@nyclegal.co.th"
              aria-label="ส่งอีเมล contact@nyclegal.co.th"
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-bold text-white backdrop-blur transition hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep"
            >
              <Mail className="h-4 w-4 text-gold" aria-hidden="true" /> Email
            </a>
          </div>
        </div>
      </section>

      {/* Services — cross-link to combo pages */}
      <section className="px-4 py-14 sm:px-6 sm:py-16">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-bold text-gold sm:text-3xl">บริการในพื้นที่ {loc.nameTh}</h2>
          <p className="mt-2 text-sm text-white/70">
            เลือกบริการเพื่อดูรายละเอียด ราคา ขั้นตอน และ FAQ เฉพาะพื้นที่ {loc.nameTh}
          </p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {serviceList.map((s) => (
              <li key={s.id}>
                <Link
                  to="/services/$service/$location"
                  params={{ service: s.slug, location: loc.slug }}
                  className="group flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4 transition hover:border-gold/40 hover:bg-white/[0.08]"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-gold" aria-hidden="true" />
                  <div className="flex-1">
                    <div className="text-sm font-bold text-white">{s.nameTh}</div>
                    <div className="mt-0.5 text-xs text-white/80">{s.shortTh}</div>
                    <div className="mt-1 text-[11px] text-gold/80">
                      เริ่มต้น ฿{s.priceFrom.toLocaleString()} · {s.turnaround}
                    </div>
                  </div>
                  <ArrowRight
                    className="mt-1 h-4 w-4 flex-none text-white/40 transition group-hover:translate-x-0.5 group-hover:text-gold"
                    aria-hidden="true"
                  />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <EntityPanel slug={loc.slug} locationName={loc.nameTh} locale="th" variant="dark" />

      {/* Deep links to long-tail combo URLs */}
      <LocationDeepLinks locationSlug={loc.slug} locale="th" />

      {/* Related */}
      {related.length > 0 && (
        <section className="border-t border-white/10 px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-xl font-bold text-gold sm:text-2xl">
              พื้นที่ใกล้เคียง · {loc.region}
            </h2>
            <ul className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((l) => (
                <li key={l.slug}>
                  <Link
                    to="/locations/$slug"
                    params={{ slug: l.slug }}
                    className="block rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm font-medium text-white/85 transition hover:border-gold/40 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                  >
                    {l.nameTh}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </main>
  );
}

/* ---------- Interactive Map + Directions (lazy + skeleton) ---------- */

function LocationMap({ loc }: { loc: ReturnType<typeof getLocation> & object }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (inView) return;
    const el = containerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "250px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  const query = loc.address
    ? `${loc.address}${loc.postalCode ? ` ${loc.postalCode}` : ""}, Thailand`
    : `${loc.nameTh}, Thailand`;

  let mapSrc: string;
  if (loc.lat && loc.lng) {
    const d = 0.01;
    const bbox = `${loc.lng - d},${loc.lat - d},${loc.lng + d},${loc.lat + d}`;
    mapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${loc.lat},${loc.lng}`;
  } else {
    mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
  }

  const directionsHref =
    loc.lat && loc.lng
      ? `https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}`
      : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}`;

  const viewLargerHref = loc.mapUrl
    ? loc.mapUrl
    : loc.lat && loc.lng
      ? `https://www.openstreetmap.org/?mlat=${loc.lat}&mlon=${loc.lng}#map=15/${loc.lat}/${loc.lng}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur">
      <div className="flex items-center justify-between gap-2 border-b border-white/10 px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest text-white/70">
        <span className="flex items-center gap-1.5 text-gold">
          <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> แผนที่
        </span>
        <a
          href={viewLargerHref}
          target="_blank"
          rel="noreferrer"
          className="text-[10px] text-white/80 underline-offset-2 hover:text-gold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded"
        >
          เปิดแผนที่ขนาดใหญ่
        </a>
      </div>
      <div
        ref={containerRef}
        className="relative aspect-[16/10] w-full bg-navy-deep/40"
        aria-busy={!loaded}
      >
        {/* Skeleton — always rendered to reserve space (CLS = 0). Hidden once loaded. */}
        <div
          aria-hidden={loaded}
          className={`absolute inset-0 flex flex-col items-center justify-center gap-2 overflow-hidden bg-gradient-to-br from-navy-deep/60 via-navy/40 to-navy-deep/60 transition-opacity duration-500 motion-reduce:transition-none ${loaded ? "pointer-events-none opacity-0" : "opacity-100"}`}
        >
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
          <MapPin className="h-8 w-8 animate-pulse text-gold/40" aria-hidden="true" />
          <span className="text-[11px] uppercase tracking-widest text-white/40">
            {inView ? "กำลังเตรียมแผนที่…" : "เลื่อนเพื่อโหลดแผนที่"}
          </span>
        </div>

        {inView && (
          <iframe
            src={mapSrc}
            title={`แผนที่ ${loc.nameTh}`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            onLoad={() => setLoaded(true)}
            className={`absolute inset-0 h-full w-full border-0 transition-opacity duration-500 motion-reduce:transition-none ${loaded ? "opacity-100" : "opacity-0"}`}
          />
        )}

        <noscript>
          <div className="absolute inset-0 flex items-center justify-center bg-navy-deep/80 p-4 text-center text-xs text-white/70">
            เปิดใช้งาน JavaScript เพื่อดูแผนที่ หรือ{" "}
            <a href={viewLargerHref} className="ml-1 text-gold underline">
              เปิดแผนที่ในแท็บใหม่
            </a>
          </div>
        </noscript>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-white/10 px-4 py-3">
        <div className="text-[11px] text-white/80">
          {loc.address ? loc.address : `บริการในพื้นที่ ${loc.nameTh}`}
        </div>
        <a
          href={directionsHref}
          target="_blank"
          rel="noreferrer"
          aria-label={`นำทางไปยัง ${loc.nameTh} ด้วย Google Maps`}
          className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-gold px-4 py-2.5 text-xs font-bold text-navy-deep transition hover:bg-gold/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep"
        >
          <Navigation className="h-4 w-4" aria-hidden="true" /> นำทาง · Google Maps
        </a>
      </div>
    </div>
  );
}
